import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, DoCheck, ElementRef, inject, Input, OnDestroy, ViewChild,
} from '@angular/core';
import {FormControl} from '@angular/forms';
import {MatAutocompleteSelectedEvent, MatAutocompleteTrigger} from '@angular/material/autocomplete';
import {Subscription} from 'rxjs';
import {ConceptOption, conceptOptionAncestors, conceptOptionLabel} from './concept-option.model';
// NB: `app-concept-select` is already taken by admin-service-import's own
// picker in this module - this one is `app-concept-picker`.

/**
 * A bounded concept-list picker for the Import module - the card header
 * event-type / reference-type picker, a sub-block role / status picker,
 * and the generic batch card all use it.
 *
 * A text field over a `mat-autocomplete`, the same widget as
 * `ConceptTermSearchComponent` and `PlaceSelectComponent`, but over the
 * preloaded `options`: typing filters them by label, and `control`
 * changes only when an option is picked. Leaving the field without a pick
 * puts the picked label back; the `–` option (when not required) is how a
 * pick is cleared.
 *
 * A hierarchic concept (`history_event_type_id`) shows its leaf name in
 * the field and as the first line of each option, indented by depth, with
 * the ancestor breadcrumb ("Produksjon ›") on a second line in the panel
 * and as a `mat-hint` under the field. A flat concept list renders as
 * plain single-line options.
 *
 * Tab with the panel open picks the highlighted option, as a `mat-select`
 * does. `control`'s disabled state carries over to the field.
 *
 * `OnPush`: `filteredOptions` is recomputed only when `options`, the typed
 * text or the picked value changes. A value or disabled state set on
 * `control` from code - with `emitEvent: false` too - shows once the
 * parent is checked (`ngDoCheck`).
 */
@Component({
  selector: 'app-concept-picker',
  templateUrl: './concept-picker.component.html',
  styleUrls: ['./concept-picker.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConceptPickerComponent implements DoCheck, OnDestroy {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);

  /** TRANS key for the field label. */
  @Input({required: true}) label!: string;
  @Input({required: true}) required = false;
  @Input({required: true})
  set options(options: ConceptOption[]) {
    this._options = options;
    this.optionsById = new Map();
    for (const option of options) {
      if (!this.optionsById.has(option.id)) {
        this.optionsById.set(option.id, option);
      }
    }
    this.searchLabels = options.map(option => conceptOptionLabel(option).toLocaleLowerCase());
    // Text being typed stays; a shown pick is looked up in the new list.
    if (this._control && typeof this.searchControl.value !== 'string') {
      this.showPicked();
    } else {
      this.applyFilter();
    }
  }
  get options(): ConceptOption[] {
    return this._options;
  }
  private _options: ConceptOption[] = [];
  private optionsById = new Map<string, ConceptOption>();
  /** Each option's lower-cased label, by index into `options`. */
  private searchLabels: string[] = [];

  @Input({required: true})
  set control(control: FormControl<string | null>) {
    this._control = control;
    this.syncDisabled();
    this.showPicked();
  }
  get control(): FormControl<string | null> {
    return this._control;
  }
  private _control!: FormControl<string | null>;

  @ViewChild(MatAutocompleteTrigger) private trigger?: MatAutocompleteTrigger;
  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;

  /** The field's own text: the picked option (shown through `displayFn`),
   * or the text typed to filter by. */
  readonly searchControl = new FormControl<string | ConceptOption | null>(null);
  /** The options whose label contains the typed text; all of them when
   * nothing is typed. */
  filteredOptions: ConceptOption[] = [];
  /** The typed text, trimmed and lower-cased; `''` when nothing is typed. */
  searchTerm = '';
  /** The `control` value the field text was last set from. */
  private shownValue: string | null | undefined;
  private keepSelectionOnMouseUp = false;
  private readonly subscriptions = new Subscription();

  optionLabel = conceptOptionLabel;

  constructor() {
    this.subscriptions.add(this.searchControl.valueChanges.subscribe(() => this.applyFilter()));
    // Capture phase, so the highlighted option is picked before the
    // autocomplete trigger's own Tab handling closes the panel without one.
    this.host.nativeElement.addEventListener('keydown', this.pickOnTab, true);
  }

  ngDoCheck(): void {
    if (!this._control) {
      return;
    }
    if (this.syncDisabled()) {
      this.cdr.markForCheck();
    }
    if (this._control.value !== this.shownValue) {
      this.showPicked();
      this.cdr.markForCheck();
    }
  }

  ngOnDestroy(): void {
    this.host.nativeElement.removeEventListener('keydown', this.pickOnTab, true);
    this.subscriptions.unsubscribe();
  }

  displayFn = (value: string | ConceptOption | null): string =>
    typeof value === 'string' ? value : conceptOptionLabel(value ?? undefined);

  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const id = (event.option.value as ConceptOption | null)?.id ?? null;
    if (id !== this._control.value) {
      this._control.markAsDirty();
      this._control.setValue(id);
    }
    this.showPicked();
  }

  /** Focusing the field selects its text, so typing replaces the picked
   * label instead of appending to it. */
  onFocus(): void {
    this.searchInput?.nativeElement.select();
  }

  /** A click that moves focus into the field from elsewhere keeps the
   * selection `onFocus` made; a click in the already-focused field (also
   * after a pick refocuses it) places the caret as usual. */
  onMouseDown(): void {
    this.keepSelectionOnMouseUp = document.activeElement !== this.searchInput?.nativeElement;
  }

  /** The mouseup that ends a focusing click would collapse the selection. */
  onMouseUp(event: MouseEvent): void {
    if (this.keepSelectionOnMouseUp) {
      this.keepSelectionOnMouseUp = false;
      event.preventDefault();
    }
  }

  /** Leaving the field while its panel is closed (e.g. after a search
   * with no hits) drops the typed text. With the panel open, a click in
   * it is a pick, and the panel's own close handles leaving. */
  onBlur(): void {
    this._control.markAsTouched();
    if (!this.trigger?.panelOpen) {
      this.dropTypedText();
    }
  }

  /** The panel closed with focus gone elsewhere (a click outside, Tab):
   * drop the typed text. A close with the field still focused (Escape)
   * keeps it for more typing. */
  onPanelClosed(): void {
    if (document.activeElement !== this.searchInput?.nativeElement) {
      this.dropTypedText();
    }
  }

  /** Left padding for an option, indenting each hierarchy level under its
   * parent. Material's own default option padding is 16px - a root keeps
   * that, a child adds one step per level below the root. */
  optionIndentPx(option: ConceptOption): number {
    return 16 + Math.max(0, (option.level ?? 1) - 1) * 16;
  }

  /** The picked concept's parent trail as a breadcrumb ("Produksjon ›",
   * "Produksjon › Støpt ›"), or `''` when it has no parent. The backend
   * sends `path` already joined with " › "; this only adds the trailing
   * chevron pointing at the picked value. */
  ancestorCrumb(option: ConceptOption | undefined): string {
    const ancestors = conceptOptionAncestors(option);
    return ancestors ? `${ancestors} ›` : '';
  }

  get selectedCrumb(): string {
    return this.ancestorCrumb(this.selectedOption());
  }

  get triggerLabel(): string {
    return conceptOptionLabel(this.selectedOption());
  }

  private readonly pickOnTab = (event: KeyboardEvent): void => {
    if (event.key === 'Tab' && this.trigger?.panelOpen) {
      this.trigger.activeOption?.select();
    }
  };

  /** Carries `control`'s disabled state over to the field; `true` when it
   * changed. */
  private syncDisabled(): boolean {
    if (this._control.disabled === this.searchControl.disabled) {
      return false;
    }
    if (this._control.disabled) {
      this.searchControl.disable({emitEvent: false});
    } else {
      this.searchControl.enable({emitEvent: false});
    }
    return true;
  }

  private dropTypedText(): void {
    if (typeof this.searchControl.value === 'string') {
      this.showPicked();
      this.cdr.markForCheck();
    }
  }

  /** Sets the field text to the picked option and clears the filter. */
  private showPicked(): void {
    this.shownValue = this._control.value;
    this.searchControl.setValue(this.selectedOption() ?? null, {emitEvent: false});
    this.applyFilter();
  }

  private applyFilter(): void {
    const value = this.searchControl.value;
    this.searchTerm = typeof value === 'string' ? value.trim().toLocaleLowerCase() : '';
    this.filteredOptions = this.searchTerm
      ? this._options.filter((_, index) => this.searchLabels[index].includes(this.searchTerm))
      : this._options;
  }

  private selectedOption(): ConceptOption | undefined {
    const value = this._control?.value;
    return value === null || value === undefined ? undefined : this.optionsById.get(value);
  }
}
